import { Tabs, TabsList, TabsTrigger } from './tabs';

/**
 * PP-21 — a single, reusable "pill" segmented control.
 *
 * Before this, the execution-plan viewer rendered its Tree/Graph/Raw choice
 * as two independent `Tabs` compositions with two different visual
 * treatments: one a muted-pill container (`ExecutionPlanPanel.tsx`), the
 * other the base `TabsTrigger` underline style with no pill at all
 * (`ExecutionPlanViewModeTabs.tsx`). This component is the one visual
 * treatment going forward for that class of control — a small, mutually
 * exclusive choice rendered as a pill inside a muted rounded track, not a
 * tab strip with an active-tab underline.
 *
 * `border-b-0 mb-0` on the trigger cancels the base `TabsTrigger`'s
 * underline treatment (`border-b-2` + `[&[data-state=active]]:border-primary`
 * + `-mb-px`, all defined in `tabs.tsx`) — `border-b-0`/`mb-0` are later in
 * the class list and in the same tailwind-merge group as the base's
 * `border-b-2`/`-mb-px`, so they win outright rather than racing a
 * conditional variant on specificity. A pill and an underline both marking
 * "active" is a double affordance, not a stronger one.
 *
 * Height is fixed at 28px track / 24px trigger (`h-7`/`h-6`), matching the
 * `xs` button variant T3.3 designated for dense toolbar/filter-bar surfaces.
 * This control always lives inside a toolbar row, never a standalone form —
 * fix round 1 (T5.3) removed a speculative `normal`/`h-8` size variant that
 * had no call site anywhere in the codebase (every consumer, including the
 * compare-pane instances added in that same fix round, wants the one dense
 * size). Add a size variant back if and when a concrete standalone-form
 * caller actually needs one.
 */

export interface SegmentedControlOption<TValue extends string> {
  value: TValue;
  label: React.ReactNode;
  testId?: string;
  disabled?: boolean;
  /** Tooltip + accessible name, for an icon-only label. */
  title?: string;
}

interface SegmentedControlProps<TValue extends string> {
  value: TValue;
  onValueChange: (value: TValue) => void;
  options: readonly SegmentedControlOption<TValue>[];
  className?: string;
  /** Replaces the trigger's horizontal padding (icon-only segments use `px-2`). */
  triggerClassName?: string;
  'aria-label'?: string;
  testId?: string;
}

export function SegmentedControl<TValue extends string>({
  value,
  onValueChange,
  options,
  className,
  triggerClassName = 'px-3',
  testId,
  ...rest
}: SegmentedControlProps<TValue>) {
  return (
    <Tabs value={value} onValueChange={(next) => onValueChange(next as TValue)} className={className} data-testid={testId}>
      <TabsList
        className="h-7 rounded-md bg-muted/30 p-0.5 flex items-stretch gap-0.5 border-0"
        aria-label={rest['aria-label']}
      >
        {options.map((option) => (
          <TabsTrigger
            key={option.value}
            value={option.value}
            disabled={option.disabled}
            title={option.title}
            aria-label={option.title}
            // A choice, not a tab strip: no tab panel is ever rendered, so Radix's `aria-controls` would point at a
            // missing element (axe `aria-valid-attr-value`, release pass 2026-10-01). Later props win over Radix's own.
            aria-controls={undefined}
            className={`h-6 ${triggerClassName} rounded-sm text-xs border-b-0 mb-0 data-[state=active]:bg-background data-[state=active]:text-foreground data-[state=active]:shadow-sm`}
            data-testid={option.testId}
          >
            {option.label}
          </TabsTrigger>
        ))}
      </TabsList>
    </Tabs>
  );
}
